import { createHighlighterCore, type HighlighterGeneric } from "shiki/core";
import { createJavaScriptRegexEngine } from "shiki/engine/javascript";

import langBash from "shiki/langs/bash.mjs";
import langGo from "shiki/langs/go.mjs";
import langHtml from "shiki/langs/html.mjs";
import langJava from "shiki/langs/java.mjs";
import langJavascript from "shiki/langs/javascript.mjs";
import langJson from "shiki/langs/json.mjs";
import langJsx from "shiki/langs/jsx.mjs";
import langPython from "shiki/langs/python.mjs";
import langRust from "shiki/langs/rust.mjs";
import langSql from "shiki/langs/sql.mjs";
import langTsx from "shiki/langs/tsx.mjs";
import langTypescript from "shiki/langs/typescript.mjs";
import langXml from "shiki/langs/xml.mjs";
import langYaml from "shiki/langs/yaml.mjs";

import { gaCodeDarkTheme, gaCodeLightTheme } from "./gaCodeThemes";

type CoreHighlighter = HighlighterGeneric<never, never>;

const SUPPORTED_LANGS = [
  "typescript",
  "javascript",
  "tsx",
  "jsx",
  "python",
  "java",
  "bash",
  "sql",
  "rust",
  "go",
  "yaml",
  "json",
  "html",
  "xml",
] as const;

// Synchronously initialized on module evaluation via top-level await.
// Guarantees highlighting is 100% available on the very first SSR/client render.
export const highlighter: CoreHighlighter = await createHighlighterCore({
  themes: [gaCodeLightTheme, gaCodeDarkTheme],
  langs: [
    langTypescript,
    langJavascript,
    langTsx,
    langJsx,
    langPython,
    langJava,
    langBash,
    langSql,
    langRust,
    langGo,
    langYaml,
    langJson,
    langHtml,
    langXml,
  ],
  engine: createJavaScriptRegexEngine(),
});

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const LANGUAGE_ALIASES: Record<string, string> = {
  ts: "typescript",
  typescript: "typescript",
  tsx: "tsx",
  js: "javascript",
  javascript: "javascript",
  jsx: "jsx",
  py: "python",
  python: "python",
  java: "java",
  sh: "bash",
  bash: "bash",
  shell: "bash",
  zsh: "bash",
  sql: "sql",
  json: "json",
  rust: "rust",
  rs: "rust",
  go: "go",
  golang: "go",
  yaml: "yaml",
  yml: "yaml",
  html: "html",
  xml: "xml",
  svg: "xml",
};

function normalizeLanguage(lang?: string): string {
  if (!lang) return "text";
  const normalized = lang.trim().toLowerCase();
  return LANGUAGE_ALIASES[normalized] ?? normalized;
}

export function highlightCode(code: string, language?: string): string {
  const normLang = normalizeLanguage(language);

  if (SUPPORTED_LANGS.includes(normLang as (typeof SUPPORTED_LANGS)[number])) {
    try {
      return highlighter.codeToHtml(code, {
        lang: normLang,
        structure: "inline",
        themes: {
          light: "ga-code-light",
          dark: "ga-code-dark",
        },
      });
    } catch (err) {
      console.warn(`Shiki failed to highlight ${normLang}`, err);
    }
  }

  return escapeHtml(code);
}
